import { useEffect } from 'react';
import { useGameStore } from '@campus/core';

/**
 * Rappel « micro ou caméra toujours allumés » quand on n'est plus sur l'onglet
 * de Placio. L'absence automatique ne coupe plus rien (useTabVisibility) : sans
 * rappel, on pouvait laisser son micro ouvert sans le savoir.
 *
 * - Notification du système, envoyée au passage en Absent automatique. Le
 *   navigateur n'autorise la demande qu'après un geste de la personne :
 *   demandée une seule fois, au premier allumage du micro ou de la caméra
 *   (askNotificationPermissionOnce, depuis GameBridge).
 * - Titre de l'onglet (« Micro allumé - Placio ») tant que l'onglet est caché
 *   et le micro ou la caméra allumé : sans autorisation, filet si les
 *   notifications sont refusées (useMediaOnTabTitle).
 */

const ASKED_KEY = 'placio:media-notif-asked';

/**
 * Placio n'est plus devant la personne : onglet caché (autre onglet, fenêtre
 * réduite) OU fenêtre qui a perdu le focus (autre fenêtre, autre application).
 * `document.hidden` seul ne voit pas le cas d'une autre fenêtre posée par-dessus
 * (constaté sur Safari et Chrome). `hasFocus()` reste vrai quand le focus est
 * dans une iframe de la page (apps intégrées).
 */
export function isPageInBackground(): boolean {
  return document.hidden || !document.hasFocus();
}

/** Appelle `fn` à chaque passage de Placio au premier plan ou en arrière-plan. Renvoie le désabonnement. */
export function onPageBackgroundChange(fn: () => void): () => void {
  document.addEventListener('visibilitychange', fn);
  window.addEventListener('blur', fn);
  window.addEventListener('focus', fn);
  return () => {
    document.removeEventListener('visibilitychange', fn);
    window.removeEventListener('blur', fn);
    window.removeEventListener('focus', fn);
  };
}
const NOTIF_TAG = 'placio-media-on';

/** « Micro allumé », « Caméra allumée », « Micro et caméra allumés », ou null si les deux sont coupés. */
export function mediaOnLabel(mic: boolean, camera: boolean): string | null {
  if (mic && camera) return 'Micro et caméra allumés';
  if (mic) return 'Micro allumé';
  if (camera) return 'Caméra allumée';
  return null;
}

/** À appeler dans le geste qui allume le micro ou la caméra (le navigateur l'exige). */
export function askNotificationPermissionOnce(): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'default') return;
  try {
    if (localStorage.getItem(ASKED_KEY)) return;
    localStorage.setItem(ASKED_KEY, '1');
  } catch { /* stockage indisponible : on demande quand même, une fois par page */ }
  void Notification.requestPermission().catch(() => { /* refus ou navigateur ancien */ });
}

/** Notification du système si Placio est en arrière-plan et que le micro ou la caméra est allumé. */
export function notifyMediaStillOn(): void {
  const { isLocalMuted, isLocalCameraOff } = useGameStore.getState();
  const mic = !isLocalMuted;
  const camera = !isLocalCameraOff;
  if (!isPageInBackground() || (!mic && !camera)) return;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const body = mic && camera
    ? 'Ton micro et ta caméra sont toujours allumés.'
    : mic ? 'Ton micro est toujours allumé.' : 'Ta caméra est toujours allumée.';
  try {
    const n = new Notification('Placio', { body, tag: NOTIF_TAG, icon: '/icon-192.png' });
    n.onclick = () => { window.focus(); n.close(); };
  } catch (err) {
    console.warn('[mediaReminder] notification impossible:', err);
  }
}

/** Titre de l'onglet tant que Placio est en arrière-plan avec le micro ou la caméra allumé. */
export function useMediaOnTabTitle(): void {
  useEffect(() => {
    const baseTitle = document.title.replace(/^.+ - (?=Placio$)/, '') || 'Placio';
    const update = () => {
      const { isLocalMuted, isLocalCameraOff } = useGameStore.getState();
      const label = isPageInBackground() ? mediaOnLabel(!isLocalMuted, !isLocalCameraOff) : null;
      const next = label ? `${label} - ${baseTitle}` : baseTitle;
      if (document.title !== next) document.title = next;
    };
    update();
    const unlisten = onPageBackgroundChange(update);
    const unsubscribe = useGameStore.subscribe((s, prev) => {
      if (s.isLocalMuted !== prev.isLocalMuted || s.isLocalCameraOff !== prev.isLocalCameraOff) update();
    });
    return () => {
      unlisten();
      unsubscribe();
      document.title = baseTitle;
    };
  }, []);
}
